import React, { useState, useEffect } from "react";
import DashboardNav from "../DashboardNavbar/DashboardNav";
import icon_people from "../../assets/img/icon_people.png";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Line } from "react-chartjs-2";
import { getAllUsers, getAllOrders } from "../../api/Api";
import { useOrder } from "../../context/OrderContext";
import dayjs from "dayjs";
import Skeleton from "@mui/material/Skeleton";
import News from "../../components/banner/News";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const theme = useTheme();

  const {
    monthlyOrders,
    todaysOrders,
    updateMonthlyOrders,
    updateTodaysOrders,
    updateTotalOrders,
    addOrder,
  } = useOrder();

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Orders Over Time",
        data: [],
        borderColor: "#1976D2",
        backgroundColor: "rgba(25, 118, 210, 0.5)",
        tension: 0.2,
        width: "100%",
      },
    ],
  });
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        setCustomers(response.data);
        if (response.data.user) {
          localStorage.setItem("user_data", JSON.stringify(response.data.user));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await getAllOrders();
        const orders = response.data.orders;

        setTotalOrders(response.data.totalOrders);
        localStorage.setItem("AllOrders", JSON.stringify(response.data.orders)); // Convert orders to JSON string

        const today = dayjs().format("YYYY-MM-DD");
        const currentMonth = dayjs().month();
        const currentYear = dayjs().year();

        const todayOrdersCount = orders.filter(
          (order) => dayjs(order.placed_at).format("YYYY-MM-DD") === today
        ).length;

        const monthOrdersCount = orders.filter((order) => {
          const orderDate = dayjs(order.placed_at);
          return (
            orderDate.month() === currentMonth &&
            orderDate.year() === currentYear
          );
        }).length;

        // Update context and local storage
        updateTodaysOrders(todayOrdersCount);
        localStorage.setItem("todaysOrders", todayOrdersCount);

        updateMonthlyOrders(monthOrdersCount);
        localStorage.setItem("monthlyOrders", monthOrdersCount);

        updateTotalOrders(response.data.totalOrders);
        localStorage.setItem("totalOrders", response.data.totalOrders);

        addOrder(orders);
        localStorage.setItem("orders", JSON.stringify(orders)); // Convert orders to JSON string
        const chartLabels = orders.map((order) =>
          dayjs(order.placed_at).format("MMM D")
        );
        const chartDataPoints = orders.map(
          () => Math.floor(Math.random() * 100) + 1
        );

        setChartData({
          labels: chartLabels,
          datasets: [
            {
              label: "Orders Over Time",
              data: chartDataPoints,
              borderColor: "#1976D2",
              backgroundColor: "rgba(25, 118, 210, 0.5)",
              tension: 0.2,
            },
          ],
        });

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };
    fetchOrders();
  }, [updateTodaysOrders, updateMonthlyOrders, updateTotalOrders, addOrder]);
  return (
    <Box sx={{ height: "100vh" }}>
      {/* Navbar */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <DashboardNav />
      </Box>
      {/* Main content */}
      <Box
        sx={{
          position: "absolute",
          top: "180px",
          left: 0,
          right: 0,
          px: 1,
          width: "90vw",
          margin: "auto",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontFamily: theme.typography.fontFamily,
            color: "#888585",
            fontWeight: "500",
          }}
        >
          Dashboard
        </Typography>

        {/* Transaction Cards */}
        <Grid container spacing={1}>
          {/* Total Customer Card */}
          <Grid item xs={12} md={4}>
            <Card
              // onClick={() => handleCardClick("/CustomerDashboard")} // Navigate to customers
              sx={{
                backgroundColor: "#E0FFF2",
                borderRadius: "10px",
                color: "#297643",
                padding: "20px",
                width: "80%",
                height: "70%",
                boxShadow: "0px 4px 4px 0px #9AEFB6",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "500",
                        marginBottom: "10px",
                        fontFamily: theme.typography.fontFamily, // Apply Poppins
                      }}
                      gutterBottom
                    >
                      Total Customer
                    </Typography>

                    {/* Data */}
                    <Box display="flex" alignItems="center" gap={"2rem"}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          color: "#888585",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {isLoading ? (
                          <Skeleton variant="circular" width={56} height={56} />
                        ) : (
                          <Avatar
                            sx={{
                              color: "#888585",
                              bgcolor: "transparent",
                              width: 60,
                              height: 60,
                              fontSize: "30px",
                            }}
                          >
                            {customers.totalUsers}
                          </Avatar>
                        )}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +2.3%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#36AB5D",
                      borderRadius: "50%",
                      width: "60px",
                      height: "60px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Monthly Order Card */}
          <Grid item xs={12} md={4}>
            <Card
              // onClick={() => handleCardClick("/OrderDashboard")} // Navigate to orders
              sx={{
                backgroundColor: "#FC7F7F",
                borderRadius: "10px",
                color: "#843838",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #E59292",
                height: "70%",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "500",
                        marginBottom: "10px",
                        fontFamily: theme.typography.fontFamily, // Apply Poppins
                      }}
                      gutterBottom
                    >
                      Monthly Order
                    </Typography>

                    {/* Data */}
                    <Box display="flex" gap={"2rem"} alignItems="center">
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          color: "#4D4C4C",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {isLoading ? (
                          <Skeleton variant="circular" width={56} height={56} />
                        ) : (
                          <Avatar
                            sx={{
                              color: "#4D4C4C",
                              bgcolor: "transparent",
                              width: 70,
                              height: 70,
                              fontSize: "30px",
                            }}
                          >
                            {monthlyOrders}
                          </Avatar>
                        )}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +3.9%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#843838",
                      borderRadius: "50%",
                      width: "60px",
                      height: "60px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Today's Online Transaction Card */}
          <Grid item xs={12} md={4}>
            <Card
              // onClick={() => handleCardClick("/Dashboard")} // Navigate to transactions
              sx={{
                backgroundColor: "#DFD8FF",
                borderRadius: "10px",
                color: "#3D2C8B",
                width: "80%",
                padding: "20px",
                boxShadow: "0px 4px 4px 0px #947FF6",
                height: "70%",
                cursor: "pointer", // Add pointer cursor on hover
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        marginBottom: "10px",
                        fontFamily: theme.typography.fontFamily, // Apply Poppins
                      }}
                      gutterBottom
                    >
                      Today's Order
                    </Typography>

                    {/* Data */}
                    <Box display="flex" gap={"2rem"} alignItems="center">
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "500",
                          color: "#888585",
                          marginRight: 1,
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        {isLoading ? (
                          <Skeleton variant="circular" width={56} height={56} />
                        ) : (
                          <Avatar
                            sx={{
                              color: "#888585",
                              bgcolor: "transparent",
                              width: 70,
                              height: 70,
                              fontSize: "30px",
                            }}
                          >
                            {todaysOrders}
                          </Avatar>
                        )}
                        {/* {todaysOrders} */}
                      </Typography>
                      <Typography
                        color="#297643"
                        sx={{
                          marginBottom: "5px",
                          fontFamily: theme.typography.fontFamily, // Apply Poppins
                        }}
                      >
                        +1.3%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      backgroundColor: "#3D2C8B",
                      borderRadius: "50%",
                      width: "60px",
                      height: "60px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={icon_people}
                      alt=""
                      style={{
                        width: "70%",
                        height: "70%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid container spacing={3} sx={{ marginTop: "1vh" }}>
            {/* Sale Analytics Section */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                // border:'1px solid black'
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  color: "#888585",
                  fontWeight: "500",
                }}
              >
                Sale Analytics
              </Typography>
              <Card
                sx={{
                  borderRadius: "10px",
                  width: "100%",
                  height: "70%",
                }}
              >
                <CardContent
                  sx={{
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      height: "300px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Line data={chartData} sx={{ width: "100%" }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* News Section */}
            {/* <Grid
              item
              xs={12}
              md={8}
              sx={{
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Card
                sx={{
                  borderRadius: "10px",
                  height: "90%",
                  maxHeight: "50vh", 
                  overflow: "hidden",
                }}
              >
                <CardContent
                  sx={{
                    padding: "20px",
                    height: "100%",
                    overflowY: "auto",
                  }}
                >
                  <News />
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
